import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faCalendarPlus } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import { formatEventDate } from "../utils/calendarInviteData.helper";
import { calendarInviteTitle } from "../utils/resolveCalendarInviteDisplay.util";

/**
 * A meeting invitation the lead sent to a **primary** mailbox (PREC-3902). The user's own mail
 * client already put it in their inbox and their calendar, so the thread says one line that it
 * happened — the mail behind it is Outlook's join-link boilerplate, not something the lead typed.
 *
 * The sibling of {@link CalendarResponseLine}; an invitation to a secondary mailbox still gets the
 * full {@link CalendarInviteCard}, because that inbox is the one nobody reads.
 */
export const CalendarInviteLine: FunctionComponent<{ conversation: Conversation }> = ({
	conversation,
}) => {
	const { t } = useTranslation();
	const title = calendarInviteTitle(conversation);
	const start = conversation.calendarInviteData?.eventStart;

	return (
		<div
			className="text-xs text-gray-700 font-normal flex items-center gap-1 flex-wrap"
			data-testid="calendar-invite-line"
		>
			<Icon icon={faCalendarPlus} />
			<span>{t("inbox.calendarInvite.received")}</span>
			{title && (
				<>
					<span aria-hidden="true">·</span>
					<span className="font-semibold">{title}</span>
				</>
			)}
			{start && (
				<>
					<span aria-hidden="true">·</span>
					<span>{formatEventDate(start)}</span>
				</>
			)}
		</div>
	);
};
